import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { School, SchoolRole } from "@/types/school";
import { extractSchoolSlug } from "@/lib/navigation";
import { useEffectOnce } from "./useEffectOnce";
import { checkIfInSchoolContext } from "./url-utils";
import { useAuth } from "./useAuth";
import { schoolRepository } from "./firestore/repositories/SchoolRepository";
import { schoolMembersRepository } from "./firestore/repositories/SchoolMemberRepository";

interface SchoolContextType {
  school: School | null;
  schoolSlug: string | null;
  error: string | null;
  inSchoolContext: boolean;
  schoolLoading: boolean;
  currentUserRole: SchoolRole | null;
  memberId: string | null;
  setRole: (role: SchoolRole) => void;
}

const localLog = (...args: unknown[]) => {
  if (process.env.NEXT_PUBLIC_ENV === "development") {
    console.log(...args);
  }
};

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

export function SchoolProvider({ children }: { children: React.ReactNode }) {
  const host = window.location.host;
  const { user } = useAuth();
  const [memberId, setMemberId] = useState<string | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [schoolSlug, setSchoolSlug] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<SchoolRole | null>(
    null
  );
  const [inSchoolContext, _] = useState<boolean>(checkIfInSchoolContext(host));
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkSchoolSlug = () => {
    setSchoolLoading(true);
    const slug = extractSchoolSlug(host);
    setSchoolSlug(slug);
  };

  const fetchUserRole = useCallback(
    async (schoolId: string) => {
      localLog("🔐 [useSchool] Fetching user role for school:", schoolId);
      if (!user || !user.uid) {
        localLog("🔐 [useSchool] No user logged in");
        setCurrentUserRole(null);
        return;
      }
      try {
        const userRole = await schoolRepository.getUserRoleInSchool(
          schoolId,
          user.email || ""
        );
        setCurrentUserRole(userRole as SchoolRole);
        if (userRole === "student") {
          const memberId = await schoolMembersRepository.getUserMemberIdByEmail(
            schoolId,
            user.email || ""
          );
          setMemberId(memberId);
        }
      } catch (err) {
        console.error("Error fetching user role:", err);
        setCurrentUserRole(null);
      }
    },
    [user]
  );

  const fetchSchoolData = useCallback(async () => {
    if (inSchoolContext && schoolSlug && !school) {
      // If we are in a school context but have no school data, try fetching public data
      try {
        const fetchedSchool = await schoolRepository.getSchoolBySlug(
          schoolSlug
        );
        if (fetchedSchool) {
          setSchool(fetchedSchool);
          if (fetchedSchool.ownerId === user?.uid) {
            setCurrentUserRole("owner");
          } else if (currentUserRole === null) {
            fetchUserRole(fetchedSchool.id);
          }
        }
      } catch (error) {
        console.error("Error fetching school by slug:", error);
        return;
      }
    }
    setSchoolLoading(false);
  }, [
    fetchUserRole,
    inSchoolContext,
    school,
    schoolSlug,
    user?.uid,
    currentUserRole,
  ]);

  useEffectOnce(() => {
    checkSchoolSlug();
  }, [inSchoolContext]);

  useEffect(() => {
    setSchoolLoading(true);
    if (schoolSlug) {
      fetchSchoolData();
    }
    setSchoolLoading(false);
  }, [fetchSchoolData, schoolSlug]);

  useEffect(() => {
    if (!user || !user.uid || !school || !school.id) {
      setCurrentUserRole(null);
      return;
    }
    localLog(
      "🔐 [useSchool] Auth state changed, refetching role...",
      user?.uid
    );
    if (school && school.id) {
      fetchUserRole(school.id);
    }
  }, [user, user?.uid, school, fetchUserRole]);

  const value: SchoolContextType = {
    school,
    schoolSlug,
    schoolLoading,
    error,
    inSchoolContext,
    currentUserRole,
    memberId,
    setRole: setCurrentUserRole,
  };

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
}

export function useSchool() {
  const context = useContext(SchoolContext);
  if (context === undefined) {
    throw new Error("useSchool must be used within a SchoolProvider");
  }
  return context;
}

export default SchoolProvider;
